import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Body, MenuPath, Note, SectionDivider, Tip, Warning, YouTubeEmbed } from '../ui';

const DrawingPosesDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary">
      {/* -- Intro -- */}
      <h2
        id="overview"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.drawingPoses.title')}
      </h2>
      <Body>{t('docs.drawingPoses.intro1')}</Body>
      <Body>
        <Trans
          i18nKey="docs.drawingPoses.intro2"
          components={{
            usec: (
              <Link
                to="/touchpose/docs/draw-groups"
                className="text-blue-link no-underline font-medium"
              />
            )
          }}
        />
      </Body>

      <YouTubeEmbed
        id="aKtAIYO29Os"
        title={t('docs.drawingPoses.overviewVideoTitle')}
      />

      <SectionDivider />

      {/* -- Activating -- */}
      <h2
        id="activating"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.drawingPoses.activatingTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.drawingPoses.activatingBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <ul className="pl-[18px] mb-[16px] font-body text-[14px] text-content-primary leading-[1.7]">
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.activatingItemPolylines"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.activatingItemMarkers"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.activatingItemHud"
            components={{ strong: <strong /> }}
          />
        </li>
      </ul>

      <SectionDivider />

      {/* -- Sketch & Pose -- */}
      <h2
        id="sketch-and-pose"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.drawingPoses.sketchTitle')}
      </h2>
      <Body>{t('docs.drawingPoses.sketchBody')}</Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawingPoses.hoverTitle')}
      </h3>
      <Body>{t('docs.drawingPoses.hoverBody')}</Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawingPoses.drawingTitle')}
      </h3>
      <Body>{t('docs.drawingPoses.drawingBody')}</Body>

      <Tip label={t('docs.drawingPoses.tipLabel')}>
        {t('docs.drawingPoses.drawingTip')}
      </Tip>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawingPoses.extendTitle')}
      </h3>
      <Body>{t('docs.drawingPoses.extendBody')}</Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawingPoses.eraseTitle')}
      </h3>
      <Body>{t('docs.drawingPoses.eraseBody')}</Body>

      <SectionDivider />

      {/* -- Choose vs sketch mode -- */}
      <h2
        id="choose-and-sketch-mode"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.drawingPoses.modeTitle')}
      </h2>
      <Body>{t('docs.drawingPoses.modeBody')}</Body>
      <ul className="pl-[18px] mb-[16px] font-body text-[14px] text-content-primary leading-[1.7]">
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.modeChoose"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.modeSketch"
            components={{ strong: <strong /> }}
          />
        </li>
      </ul>

      <SectionDivider />

      {/* -- Tool settings -- */}
      <h2
        id="tool-settings"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.drawingPoses.toolSettingsTitle')}
      </h2>
      <Body>{t('docs.drawingPoses.toolSettingsBody')}</Body>

      <ul className="pl-[18px] mb-[16px] font-body text-[14px] text-content-primary leading-[1.7]">
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.settingTouchNode"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.settingLayer"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.settingStrokeColor"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.settingSmoothStroke"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.settingHideDuringDraw"
            components={{ strong: <strong /> }}
          />
        </li>
      </ul>

      <SectionDivider />

      {/* -- Hotkeys -- */}
      <h2
        id="hotkeys"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.drawingPoses.hotkeysTitle')}
      </h2>
      <Body>{t('docs.drawingPoses.hotkeysBody')}</Body>
      <ul className="pl-[18px] mb-[16px] font-body text-[14px] text-content-primary leading-[1.7]">
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.hotkeyPose"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.hotkeyClear"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.hotkeyEsc"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.hotkeyTab"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.hotkeyUndo"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawingPoses.hotkeyErase"
            components={{ strong: <strong /> }}
          />
        </li>
      </ul>

      <Note label={t('docs.drawingPoses.noteLabel')}>
        {t('docs.drawingPoses.hotkeysNote')}
      </Note>

      <SectionDivider />

      {/* -- Referenced rigs / multi-rig -- */}
      <h2
        id="referenced-rigs"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.drawingPoses.referencedRigsTitle')}
      </h2>
      <Body>{t('docs.drawingPoses.referencedRigsBody')}</Body>

      <Warning label={t('docs.drawingPoses.warningLabel')}>
        {t('docs.drawingPoses.referencedRigsWarning')}
      </Warning>
    </article>
  );
};

export default DrawingPosesDoc;
